import { createGlobalStyle } from 'styled-components';
import { rgba } from 'polished';

// CONSTANTS
import colors, {
  WHITE,
  MAIN_BLACK,
  PORCELAIN,
  WOODSMOKE,
  WOODSMOKE_2,
  PURPLE,
  AZURE_RADIANCE,
  EDWARD,
} from '@/constants/colors';
import breakpoints from '@/constants/breakpoints';

// ASSETS
import '@/assets/fonts/icomoon/style.css';

import commonPartnerStyles from './partners/commonPartnerStyles';
import bridgewiredStyles from './partners/bridgewiredStyles';
import cspireStyles from './partners/cspireStyles';
import epbStyles from './partners/epbStyles';
import fiberMeFastStyles from './partners/fibermefastStyles';
import htcStyles from './partners/htcStyles';
import medinaStyles from './partners/medinaStyles';
import metronetStyles from './partners/metronetStyles';
import quantumStyles from './partners/quantumStyles';
import windstreamStyles from './partners/windstreamStyles';
import wintekStyles from './partners/wintekStyles';

const fontFamily = "'Gilroy', 'Roboto', 'Helvetica', 'Arial', sans-serif";

const AppWrapper = createGlobalStyle`

  @font-face {
    font-family: 'Gilroy';
    src: local('Gilroy Regular'),
       url(${`/fonts/Gilroy/Gilroy-Regular.woff`}) format('woff');
    font-weight: 400;
    font-style: normal;
  }

  @font-face {
    font-family: 'Gilroy';
    src: local('Gilroy Medium'),
       url(${`fonts/Gilroy/Gilroy-Medium.woff`}) format('woff');
    font-weight: 500;
    font-style: normal;
  }

  @font-face {
    font-family: 'Gilroy';
    src: local('Gilroy SemiBold'),
       url(${`/fonts/Gilroy/Gilroy-SemiBold.woff`}) format('woff');
    font-weight: 600;
    font-style: normal;
  }

  @font-face {
    font-family: 'Gilroy';
    src: local('Gilroy Bold'),
       url(${`/fonts/Gilroy/Gilroy-Bold.woff`}) format('woff');
    font-weight: 700;
    font-style: normal;
  }


  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Firefox < 16 */
  @-moz-keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Safari, Chrome and Opera > 12.1 */
  @-webkit-keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Internet Explorer */
  @-ms-keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Opera < 12.1 */
  @-o-keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  * {
    box-sizing: border-box;
  }

  html {
    min-height: 100%;
    font-size: 10px;
    -webkit-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    //font-family: ${p => p.theme.typography.fontFamily || fontFamily};
    font-size: 1.8rem;
    line-height: 3rem;
    font-weight: 400;
    background-color: ${p => p.theme.palette.background.default || colors.bg};
    color: ${p => p.theme.palette.text.primary || colors.text};
    margin: 0;
    padding: 0;
  }

  .App {
    min-width: 375px;
    //overflow-x: hidden;
    //overflow-y: hidden;
  }

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active  {
      -webkit-box-shadow: 0 0 0 1000px transparent inset;
      transition: background-color 5000s ease-in-out 0s;
  }

  a {
    text-decoration: none;
  }

  // Typography
  h1 {
    font-size: 6rem;
    line-height: 6rem;
    font-weight: 400;
  }

  h2 {
    font-size: 4.2rem;
    line-height: 4.6rem;
    font-weight: 400;
  }

  h3 {
    font-size: 3.6rem;
    line-height: 3.6rem;
    font-weight: 700;
  }

  h4 {
    font-size: 3rem;
    line-height: 3rem;
    font-weight: 700;
  }

  h5 {
    font-size: 2rem;
    line-height: 2rem;
    font-weight: 700;
  }

  h6 {
    font-size: 1.8rem;
    line-height: 1.8rem;
    font-weight: 700;
  }

  p {
    margin-top: 0;
    margin-bottom: 10px;
  }

  .p1, .p2, .p3, .p4, .p5 {
    margin: 0;
  }

  .p1 {
    font-size: 2.2rem;
    line-height: 3rem;
    font-weight: 400;
  }

  .p2 {
    font-size: 2rem;
    line-height: 2rem;
    font-weight: 500;
  }

  .p3 {
    font-size: 1.8rem;
    line-height: 2.4rem;
    font-weight: 400;
  }

  .p4 {
    font-size: 1.6rem;
    line-height: 2.4rem;
    font-weight: 500;
  }

  .p5 {
    font-size: 1.4rem;
    line-height: 2rem;
    font-weight: 400;
  }

  .content-visibility {
    &--auto {
      content-visibility: auto;
    }

    &--hidden {
      content-visibility: hidden;
    }
  }

  .subtitle-secondary {
    font-size: 1.4rem;
    line-height: 1.4rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 7px;
    margin-bottom: 20px;
  }

  .text-center {
    text-align: center;
  }

  .text-capitalize {
    text-transform: capitalize;
  }

  .hidden {
    display: none;
  }

  .hiddenVisibility {
    visibility: hidden;
  }

  .m-r-10 {
    margin-right: 10px;
  }

  .m-r-15 {
    margin-right: 15px;
  }

  .m-b-5 {
    margin-bottom: 5px;
  }

  .m-b-10 {
    margin-bottom: 10px;
  }

  .m-b-15 {
    margin-bottom: 15px;
  }

  .m-b-25 {
    margin-bottom: 25px;
  }

  .m-t-10 {
    margin-top: 10px;
  }

  .m-t-15 {
    margin-top: 15px;
  }

  .m-t-20 {
    margin-top: 20px;
  }

  .m-t-30 {
    margin-top: 30px;
  }

  .m-t-40 {
    margin-top: 40px;
  }

  .m-b-20 {
    margin-bottom: 20px;
  }

  .m-b-30 {
    margin-bottom: 30px !important;
  }

  .pos-relative {
    position: relative;
  }

  .noselect {
    -webkit-touch-callout: none; /* iOS Safari */
      -webkit-user-select: none; /* Safari */
       -khtml-user-select: none; /* Konqueror HTML */
         -moz-user-select: none; /* Old versions of Firefox */
          -ms-user-select: none; /* Internet Explorer/Edge */
              user-select: none; /* Non-prefixed version, currently
                                    supported by Chrome, Opera and Firefox */
  }

  .cut-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Page Content
  .page-content {
    padding: 30px 30px 50px;
    height: 100%;

    @media screen and (max-width: ${breakpoints.sm}) {
      padding: 30px 15px  50px;
    }

    &--no-padding {
      padding: 0;
    }
  }

  .section {
    padding: 110px 0;

    @media screen and (max-width: ${breakpoints.sm}) {
      padding: 70px 0;
    }

    @media screen and (max-width: ${breakpoints.xs}) {
      padding: 60px 0;
    }

    &--grey {
      background-color: ${PORCELAIN};
    }

    &--white {
      background-color: ${WHITE};
    }

    &--dark {
      background-color: ${MAIN_BLACK};
      color: ${WHITE};
    }

    &--deep-dark {
      background-color: ${WOODSMOKE};
      color: ${WHITE};
    }

    &--deep-dark-2 {
      background-color: ${WOODSMOKE_2};
      color: ${WHITE};
    }
  }

  // Static Pages
  .static-pages {
    &__wrapper {
      padding: 83px 0 130px;

      @media screen and (max-width: ${breakpoints.sm}) {
        padding: 53px 0 90px;
      }

      @media screen and (max-width: ${breakpoints.xs}) {
        padding: 23px 0 50px;
      }
    }

    &__content {
      max-width: 920px;
      margin: 0 auto;
    }

    h1, h2 {
      padding: 34px 0 12px;

      @media (max-width: ${breakpoints.xs}) {
        padding-top: 24px;
      }
    }

    h3, h4 {
      padding: 30px 0 3px;

      @media (max-width: ${breakpoints.xs}) {
        padding-top: 20px;
      }
    }

    h1 {
      font-weight: 600;
    }

    h2 {
      line-height: 4.9rem;
      font-weight: 600;
    }

    h4 {
      line-height: 3.5rem;
      font-weight: 600;
    }

    h5 {
      font-size: 2.4rem;
      line-height: 2.8rem;
      font-weight: 600;
    }

    h6 {
      line-height: 2.1rem;
      font-weight: 600;
    }

    a {
      color: ${colors.primary};
      font-weight: 700;

      word-break: break-word;
      text-decoration: none;
    }

    hr {
      margin: 8px 0;
    }

    p {
      padding: 10px 0 20px;
      line-height: 3rem;
    }

    ul {
      padding: 30px 0 20px;
      margin: 0;
      list-style: none;

      @media (max-width: ${breakpoints.xs}) {
        padding: 20px 0 10px;
      }

      li {
        margin-bottom: 40px;
        display: flex;
        align-items: flex-start;

        &:last-of-type {
          margin-bottom: 0;
        }

        h5 {
          padding: 0;
          margin: 0;
          margin-bottom: 5px;
        }

        p {
          &:first-of-type {
            padding-top: 0;
          }

          &:last-of-type {
            padding-bottom: 0;
          }
        }



        &:before {
          content: '\\2022';
          color: ${colors.text};
          font-weight: bold;
          display: inline-block;
          padding-right: 12px;
          font-size: 2.2rem;

          @media (max-width: ${breakpoints.xs}) {
            padding-right: 8px;
          }
        }
      }
    }

    .title-container {
      padding-bottom: 60px;
    }

    .sub-section {
      h5 {
        padding: 20px 0 0;
      }

      h6 {
        padding: 35px 0 1px;
        font-weight: 600;
      }

      ul, p {
        padding: 5px 0 10px;
      }

      li {
        margin-bottom: 0;

        &:before {
          padding-right: 12px;
          font-size: 16px;

          @media (max-width: ${breakpoints.xs}) {
            padding-right: 8px;
          }
        }
      }
    }

    .pt-10 {
      padding-top: 10px !important;
    }

    .pb-15 {
      padding-bottom: 15px !important;
    }

    .pb-20 {
      padding-bottom: 20px !important;
    }
  }

  // Text color
  .text-color {

    &--default {
      color: ${colors.text}
    }

    &--white {
      color: ${WHITE};
    }

    &--purple {
      color: ${PURPLE};
    }

    &--primary {
      color: ${p => p.theme.palette.primary.main};
    }

    &--error {
      color: ${p => p.theme.palette.error.main};
    }

    &--blue {
      color: ${AZURE_RADIANCE};
    }

    &--grey {
      color: ${EDWARD};
    }
  }

  // Cursor
  .cursor {
    &--default {
      cursor: default;
    }
    &--pointer {
      cursor: pointer;
    }
  }

  // Markers
  .marker {
    &--el,
    &:before {
      position: absolute;
      top: -15px;
      left: -15px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 50px;
      height: 50px;
      color: ${p => p.theme.palette.primary.contrastText};
      background: ${p => p.theme.palette.primary.main};
      font-size: 10px;
      font-weight: 600;
      border-radius: 25px;
      z-index: 1;
      clip-path: polygon(
        100% 50%,
        84.70057015% 54.56841673%,
        91.05184762% 60.99980942%,
        82.33578364% 63.39392013%,
        93.30127019% 75%,
        77.76736691% 71.30665002%,
        80.0520382% 80.0520382%,
        71.30665002% 77.76736691%,
        75% 93.30127019%,
        63.39392013% 82.33578364%,
        60.99980942% 91.05184762%,
        54.56841673% 84.70057015%,
        50% 100%,
        45.43158327% 84.70057015%,
        39.00019058% 91.05184762%,
        36.60607987% 82.33578364%,
        25% 93.30127019%,
        28.69334998% 77.76736691%,
        19.9479618% 80.0520382%,
        22.23263309% 71.30665002%,
        6.69872981% 75%,
        17.66421636% 63.39392013%,
        8.94815238% 60.99980942%,
        15.29942985% 54.56841673%,
        0% 50%,
        15.29942985% 45.43158327%,
        8.94815238% 39.00019058%,
        17.66421636% 36.60607987%,
        6.69872981% 25%,
        22.23263309% 28.69334998%,
        19.9479618% 19.9479618%,
        28.69334998% 22.23263309%,
        25% 6.69872981%,
        36.60607987% 17.66421636%,
        39.00019058% 8.94815238%,
        45.43158327% 15.29942985%,
        50% 0%,
        54.56841673% 15.29942985%,
        60.99980942% 8.94815238%,
        63.39392013% 17.66421636%,
        75% 6.69872981%,
        71.30665002% 22.23263309%,
        80.0520382% 19.9479618%,
        77.76736691% 28.69334998%,
        93.30127019% 25%,
        82.33578364% 36.60607987%,
        91.05184762% 39.00019058%,
        84.70057015% 45.43158327%
      );
    }

    &--static {
      position: static;
      display: inline-flex;
      margin-bottom: 7px;
    }

    &--small {
      font-size: 9px;
    }

    &--super-deal {
      &:before {
        content: 'DEAL';
      }
    }
  }

  // Marker Labels
  .marker-label {
    position: absolute;
    top: 0;
    right: 0;
    color: ${p => p.theme.palette.primary.contrastText};
    background: ${p => p.theme.palette.primary.main};
    height: 28px;
    width: 28px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .breadcrumbs {
    &--light {
      a {
        color: ${rgba(WHITE, 0.4)};
      }

      span {
        color: ${WHITE};
      }
    }

    &--dark {
      .MuiLink-root {
        color: ${rgba(MAIN_BLACK, 0.4)};
      }

      span {
        color: ${MAIN_BLACK};
      }
    }
  }

  // MARKDOWN STYLES
  .markdown {
    pre {
      white-space: inherit;
    }
  }

  // PARTNER EXTRA STYLES
  ${p => {
    if (p.partner) {
      return commonPartnerStyles;
    }
    return '';
  }}

  ${p => {
    let partnerStyles = '';

    switch (p.partner) {
      case 'bridgewired':
        partnerStyles = bridgewiredStyles;
        break;
      case 'cspire':
        partnerStyles = cspireStyles;
        break;
      case 'epb':
        partnerStyles = epbStyles;
        break;
      case 'fibermefast':
        partnerStyles = fiberMeFastStyles;
        break;
      case 'htc':
        partnerStyles = htcStyles;
        break;
      case 'medina':
        partnerStyles = medinaStyles;
        break;
      case 'metronet':
        partnerStyles = metronetStyles(p.partner.theme);
        break;
      case 'quantum':
        partnerStyles = quantumStyles;
        break;
      case 'windstream':
        partnerStyles = windstreamStyles;
        break;
      case 'wintek':
        partnerStyles = wintekStyles;
        break;
      default:
        partnerStyles = '';
        break;
    }

    return p.extraStyles ? partnerStyles : '';
  }}

`;

export default AppWrapper;
